import axios from 'axios';
import { PaginatedResponse } from '../../types';

// Use relative URLs to go through React proxy instead of direct API calls
const API_BASE_URL = '/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Track if we're already handling a 403 to prevent recursion
let isHandling403 = false;

export const SESSION_EXPIRED_EVENT = 'rentegic:session-expired';

// Add response interceptor to handle 403 errors (authentication required)
api.interceptors.response.use(
  (response) => {
    isHandling403 = false;
    return response;
  },
  (error) => {
    if (error.response?.status === 403) {
      const url = error.config?.url || '';

      if (!isHandling403 && !url.includes('/auth/login')) {
        isHandling403 = true;
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      }
    }
    return Promise.reject(error);
  }
);

// ============================================================================
// Shared Common Component Types
// ============================================================================
// These types are shared across multiple common components and represent
// structural data types (not business logic or component-specific props).

/**
 * Sort option representing a column sort configuration
 * Used by SortBar and SearchListBarLegacy components
 */
export interface SortOption {
  field: string;
  direction: 'asc' | 'desc';
  label: string;
}

/**
 * Sort column configuration representing available sortable columns
 * Used by SortBar and SearchListBarLegacy components
 */
export interface SortColumn {
  field: string;
  label: string;
}

/**
 * Search criterion representing a field-specific search
 * Used by SearchListBar component
 */
export interface SearchCriterion {
  field: string;      // Field key (e.g., 'firstName', 'email')
  label: string;      // Display label (e.g., 'First Name')
  type: 'text' | 'number'; // Field type for initial implementation
}

/**
 * Searchable field configuration for search components
 * Used by SearchListBar component
 */
export interface SearchableField {
  key: string;        // Field key matching backend
  label: string;      // Display label for user
  type: 'text' | 'number'; // Field type
  placeholder?: string; // Optional placeholder
}

